import { isNaN } from 'lodash'
import {
	ERROR_CODE,
	isNumberLatinicCyrillic,
	isNumValidator,
	lengthValidator,
	messageTranslator,
	passportStringExcludeInvalidChars,
	paymentAccountRegExp,
	ValidateInnPayload,
	ValidatorOptions,
	ValidatorResult,
} from 'Modules'

export const validateOkvedCodes = (value: string) => {
	const validate = new ValidatorResult()
	if (validate.error.message.length) {
		return validate
	}
	const codesArray = value.replace(/ /g, '').split(',')
	codesArray.forEach((item) => {
		const digitFirst = Number.parseInt(item.substr(0, 1), 10)
		const digitSecond = Number.parseInt(item.substr(1, 1), 10)
		if (
			!isNaN(digitFirst) &&
			!isNaN(digitSecond) &&
			(digitFirst || digitSecond) &&
			!validate.error.code
		) {
			validate.result = true
		} else {
			validate.error.code = ERROR_CODE.BAD_FORMAT
			validate.error.message = messageTranslator('okved.wrong_format')
		}
	})
	return validate
}

export const validateInn = (value: string, options?: ValidatorOptions<ValidateInnPayload>) => {
	let inn = value
	const validate = new ValidatorResult()
	const isNum = isNumValidator(value)
	if (!isNum.result) {
		return isNum
	}
	if (typeof inn !== 'string') {
		inn = ''
	}

	if (Number(inn) <= 0) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
		validate.error.message = messageTranslator('inn.no_nulls')
	} else if ((options?.payload?.isTwelve ? [12] : [10, 12]).indexOf(inn.length) === -1) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
		validate.error.message = options?.payload?.isTwelve
			? messageTranslator('inn.only_12')
			: messageTranslator('inn.only_10_or_12')
	} else {
		const checkDigit = (inn: string, coefficients: number[]) => {
			let verify = 0
			coefficients.forEach((coef, idx) => {
				verify += coef * Number(inn[idx])
				return null
			})
			return (verify % 11) % 10
		}

		switch (inn.length) {
			case 10:
				if (checkDigit(inn, [2, 4, 10, 3, 5, 9, 4, 6, 8]) === parseInt(inn[9], 10)) {
					validate.result = true
				}
				break
			case 12:
				if (
					checkDigit(inn, [7, 2, 4, 10, 3, 5, 9, 4, 6, 8]) === parseInt(inn[10], 10) &&
					checkDigit(inn, [3, 7, 2, 4, 10, 3, 5, 9, 4, 6, 8]) === parseInt(inn[11], 10)
				) {
					validate.result = true
				}
				break
			default:
				break
		}
		if (!validate.result) {
			validate.error.code = ERROR_CODE.BAD_FORMAT
			validate.error.message = messageTranslator('inn.invalid_value')
		}
	}
	return validate
}

export const validateSnils = (value: string) => {
	const validate = new ValidatorResult()
	const isNum = isNumValidator(value)
	const isLength = lengthValidator(value, {
		messageError: 'common.strict_length_digits',
		payload: { length: 11 },
	})
	if (!isNum.result) {
		return isNum
	}
	if (!isLength.result) {
		return isLength
	}

	let sum = 0
	let cDigit = 0
	for (let i = 0; i < 9; i += 1) {
		sum += parseInt(value[i], 10) * (9 - i)
	}
	if (sum < 100) {
		cDigit = sum
	} else if (sum > 101) {
		cDigit = sum % 101
		if (cDigit === 100) {
			cDigit = 0
		}
	}
	if (cDigit === parseInt(value.slice(-2), 10)) {
		validate.result = true
	}
	if (!validate.result) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
		validate.error.message = messageTranslator('snils.invalid_value')
	}
	return validate
}

export const validateAccountNumber = (value: string) => {
	const validate = new ValidatorResult()
	const isNum = isNumValidator(value)
	const isLength = lengthValidator(value, { payload: { length: 20 } })
	if (!isNum.result) {
		return isNum
	}
	if (!isLength.result) {
		return isLength
	}
	validate.result = true
	return validate
}

export const validateBIK = (value: string) => {
	const validate = new ValidatorResult()
	const isNum = isNumValidator(value)
	const isLength = lengthValidator(value, {
		messageError: 'common.strict_length_digits',
		payload: { length: 9 },
	})
	if (!isNum.result) {
		return isNum
	}
	if (!isLength.result) {
		return isLength
	}
	validate.result = true
	return validate
}

export const validateSelectBIK = (value: string) => {
	const validate = new ValidatorResult()
	const bik = value.split(' ')
	const res = bik[0]
	const isNum = isNumValidator(res)
	const isLength = lengthValidator(res, {
		messageError: 'common.strict_length_digits',
		payload: { length: 9 },
	})
	if (!isNum.result) {
		return isNum
	}
	if (!isLength.result) {
		return isLength
	}
	validate.result = true
	return validate
}

export const validateKPP = (value: string) => {
	const validate = new ValidatorResult()
	const isNum = isNumValidator(value)
	const isLength = lengthValidator(value, {
		messageError: 'common.strict_length_digits',
		payload: { length: 9 },
	})
	if (!isNum.result) {
		return isNum
	}
	if (!isLength.result) {
		return isLength
	}
	validate.result = true
	return validate
}

export const validateOKOPF = (value: string) => {
	const validate = new ValidatorResult()
	const isNum = isNumValidator(value)
	const isLength = lengthValidator(value, {
		messageError: 'common.strict_length_digits',
		payload: { length: 5 },
	})
	if (!isNum.result) {
		return isNum
	}
	if (!isLength.result) {
		return isLength
	}
	validate.result = true
	return validate
}

export const validateOKFS = (value: string) => {
	const validate = new ValidatorResult()
	const isNum = isNumValidator(value)
	const isLength = lengthValidator(value, {
		messageError: 'common.strict_length_digits',
		payload: { length: 2 },
	})
	if (!isNum.result) {
		return isNum
	}
	if (!isLength.result) {
		return isLength
	}
	validate.result = true
	return validate
}

export const validatePaymentAccount = (value: string) => {
	const validate = new ValidatorResult()
	if (value.length > 11) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
		validate.error.message = messageTranslator('account.length_exceeded')
	} else if (!paymentAccountRegExp.test(value)) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
		validate.error.message = messageTranslator('account.invalid_value')
	} else {
		validate.result = true
	}
	return validate
}

export const validateOktmo = (value: string) => {
	const validate = new ValidatorResult()
	const isNum = isNumValidator(value)
	if (!isNum.result) {
		return isNum
	}
	if (value.length !== 8 && value.length !== 11) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
		validate.error.message = messageTranslator('oktmo.only_8_or_11')
	} else {
		validate.result = true
	}
	return validate
}

export const validateKbk = (value: string) => {
	const validate = new ValidatorResult()
	const isNumLatCyr = isNumberLatinicCyrillic(value)
	const isLength = lengthValidator(value, {
		messageError: 'common.strict_length_digits',
		payload: { length: 20 },
	})
	if (!isNumLatCyr.result) {
		return isNumLatCyr
	}
	if (!isLength.result) {
		return isLength
	}
	validate.result = true
	return validate
}

export const validatePurchaseCode = (value: string) => {
	const validate = new ValidatorResult()
	const isNum = isNumValidator(value)
	if (!isNum.result) {
		return isNum
	}
	if (value.length !== 11 && value.length !== 18 && value.length !== 19) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
		validate.error.message = messageTranslator('purchase_code.wrong_length')
	} else {
		validate.result = true
	}
	return validate
}

export const validateOgrn = (value: string) => {
	const validate = new ValidatorResult()
	const isNum = isNumValidator(value)
	if (!isNum.result) {
		return isNum
	}

	const checkControlDigit = (value: string) => {
		const controlDigit = Number(value.slice(0, value.length - 1)) % (value.length - 2)
		return value.slice(-1) === String(controlDigit).slice(-1)
	}

	if (value.length !== 13 && value.length !== 15) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
		validate.error.message = messageTranslator('ogrn.wrong_length')
	} else if (!checkControlDigit(value)) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
		validate.error.message = messageTranslator('ogrn.wrong_value')
	} else {
		validate.result = true
	}
	return validate
}

export const validateOkato = (value: string) => {
	const validate = new ValidatorResult()
	const isNum = isNumValidator(value)
	if (!isNum.result) {
		return isNum
	}
	if (value.length < 2 || value.length > 11) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
		validate.error.message = messageTranslator('okato.wrong_value')
	} else {
		validate.result = true
	}
	return validate
}

export const validateOkpo = (value: string) => {
	const validate = new ValidatorResult()
	const isNum = isNumValidator(value)
	if (!isNum.result) {
		return isNum
	}
	if (value.length !== 8 && value.length !== 10) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
		validate.error.message = messageTranslator('okpo.wrong_value')
	} else {
		validate.result = true
	}
	return validate
}

export const validatePassportString = (value: string) => {
	const validate = new ValidatorResult()
	if (passportStringExcludeInvalidChars.test(value)) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
		validate.error.message = messageTranslator('common.cyrillic_digits_and_spec_symbols')
	} else {
		validate.result = true
	}
	return validate
}
